import { useCubitBuilder } from 'hooks/cubits'
import { useAuth, useRepository } from 'hooks/auth'
import { useBreakoutUser } from 'hooks/profile'
import { ClickWrapsCubit } from '@breakoutlearning/firebase-repository/cubits/ClickWrapsCubit'
import type { ClickWrap } from '@breakoutlearning/firebase-repository/models/ClickWrap'
import { FullPageSpinner } from 'components/Spinner'
import { observer } from 'mobx-react-lite'
import { useTranslationTyped } from 'i18n/i18n'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { LinkOutIcon } from 'components/icons/LinkOut'
import { useRootStore } from 'hooks/rootStore'
import { useCallback } from 'react'
import { MultiLineText } from 'components/breakout/MultiLineText'

interface UserAgreementsProps {
  children: React.ReactNode
}

export const UserAgreements = observer(function UserAgreements({
  children,
}: UserAgreementsProps) {
  const repository = useRepository()
  const user = useBreakoutUser()
  const cubit = useCubitBuilder(
    () =>
      new ClickWrapsCubit(repository, {
        user,
      }),
    []
  )

  if (cubit.clickWrapState === 'loading') {
    return <FullPageSpinner />
  }

  if (cubit.clickWrapState === 'must_accept') {
    return (
      <UserAgreementsList
        userAgreements={cubit.clickWrapsToAccept}
        onAccept={() => cubit.acceptClickWraps()}
      />
    )
  }

  return children
})

interface UserAgreementsListProps {
  userAgreements: ClickWrap[]
  onAccept: () => void
}

const UserAgreementsList = ({
  userAgreements,
  onAccept,
}: UserAgreementsListProps) => {
  const { tt } = useTranslationTyped()
  return (
    <div className="flex flex-col gap-5">
      <div>
        <img
          src="/assets/images/logo_large.png"
          width={200}
          data-testid="logo"
          tabIndex={0}
          alt={tt.breakout_learning()}
          className="object-contain"
        />
      </div>
      <h1 className="text-display-medium">
        {tt.click_wraps.review_and_accept()}
      </h1>
      <div className="w-[600px] rounded-2xl bg-surface-dim p-5 text-on-surface">
        <p>
          <MultiLineText
            string={tt.click_wraps.review_and_accept_description()}
          />
        </p>
        <ul className="my-5 list-disc pl-5">
          {userAgreements.map((userAgreement) => (
            <li key={userAgreement.id} className="mb-1">
              <a
                href={userAgreement.data.clickWrapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 underline"
              >
                {userAgreement.data.clickWrapName}
                <LinkOutIcon size={16} />
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-row-reverse justify-between gap-5">
        <BreakoutButton kind="accent" onClick={() => onAccept()}>
          {tt.click_wraps.accept_button()}
        </BreakoutButton>
        <LogoutButton />
      </div>
    </div>
  )
}

function LogoutButton() {
  const { tt } = useTranslationTyped()
  const auth = useAuth()
  const rootStore = useRootStore()
  const repository = useRepository()
  const breakoutUser = useBreakoutUser()
  const logUserOut = useCallback(() => {
    breakoutUser.dispose()
    rootStore.impersonationInfo && rootStore.stopImpersonation()
    repository.logout()

    auth.signOut().finally(async () => {
      const { queryParams } = rootStore.router
      if (
        queryParams?.redirectURL &&
        typeof queryParams.redirectURL === 'string'
      ) {
        // if redirect URL param present, replace the current URL with the redirect URL
        const redirectURL = decodeURIComponent(queryParams.redirectURL)
        return (window.location.href = redirectURL)
      }
      rootStore.navigateTo('home')
    })
  }, [auth, rootStore, repository, breakoutUser])
  return (
    <BreakoutButton kind="secondary" onClick={logUserOut}>
      {tt.click_wraps.logout()}
    </BreakoutButton>
  )
}
